import * as React from "react";
import { cn, DataObserveKey, VisualComponent, Button, ButtonStyle } from "@siteimprove/fancylib";
import * as scss from "./action-bar.scss";

type ActionBarProps = {
	/** The primary action */
	primary: FormAction;
	/** Possible secondary actions */
	secondaries?: FormAction[];
	/** Possible cancellation action */
	cancel?: FormAction;
	/** An Action Bar without any padding */
	noPadding?: boolean;
	/** An Action Bar without any background */
	noBackground?: boolean;
	/** Possible destructive action, use `DestructiveActionBar` if this is the primary action  */
	destructive?: FormAction;
	/**
	 * A token that identifies if an action is in progress, disabling all actions.
	 * The token is acquired via the onClick handler from each action
	 */
	inProgressAction?: string | null;
} & DataObserveKey &
	VisualComponent;

type FormAction = {
	children: React.ReactNode;
	disabled?: boolean;
	"data-observe-key"?: string;
	type?: "submit" | "reset" | "button";
	form?: string;
} & (
	| { onClick?: (identityToken: string, e: React.MouseEvent<HTMLButtonElement>) => void }
	| { href?: string; openNew?: boolean }
);

export function ActionBar(props: ActionBarProps): JSX.Element {
	const {
		primary,
		secondaries,
		cancel,
		noPadding,
		noBackground,
		destructive,
		inProgressAction,
		className,
		style,
	} = props;
	return (
		<div
			className={cn(
				scss.actions,
				noPadding && scss.noPadding,
				noBackground && scss.noBackground,
				className
			)}
			style={style}
			data-observe-key={props["data-observe-key"]}
		>
			{destructive && (
				<div className={scss.destructive}>
					{renderButton("destructive", destructive, inProgressAction)}
				</div>
			)}

			{cancel && renderButton("default", cancel, inProgressAction)}

			{secondaries?.map((act, idx) => renderButton("secondary", act, inProgressAction, idx))}

			{renderButton("primary", primary, inProgressAction)}
		</div>
	);
}

export type DestructiveActionBarProps = {
	/** The primary destructive action */
	destructive: FormAction;
	/** The alternative cancellation action */
	cancel: FormAction;
	/**
	 * A token that identifies if an action is in progress, disabling all actions.
	 * The token is acquired via the onClick handler from each action
	 */
	inProgressAction?: string | null;
} & DataObserveKey &
	VisualComponent;

export function DestructiveActionBar(props: DestructiveActionBarProps): JSX.Element {
	const { inProgressAction, destructive, cancel, className, style } = props;
	return (
		<div
			className={cn(scss.actions, className)}
			style={style}
			data-observe-key={props["data-observe-key"]}
		>
			{cancel && renderButton("default", cancel, inProgressAction)}
			{destructive && renderButton("destructive", destructive, inProgressAction)}
		</div>
	);
}

function renderButton(
	variant: ButtonStyle,
	action: FormAction & DataObserveKey,
	inProgressAction: string | undefined | null,
	idx = 0
) {
	const actionId = `${variant}-${idx}`;

	if ("href" in action) {
		return (
			<Button
				data-observe-key={action["data-observe-key"]}
				variant={variant}
				disabled={Boolean((inProgressAction && inProgressAction !== actionId) || action.disabled)}
				loading={inProgressAction === actionId}
				href={action.href}
				key={actionId}
				openNew={action.openNew}
				form={action.form}
			>
				{action.children}
			</Button>
		);
	}

	return (
		<Button
			data-observe-key={action["data-observe-key"]}
			variant={variant}
			type={action.type}
			disabled={Boolean((inProgressAction && inProgressAction !== actionId) || action.disabled)}
			loading={inProgressAction === actionId}
			onClick={(e) => {
				if (!inProgressAction && "onClick" in action && action.onClick) {
					action.onClick(actionId, e as React.MouseEvent<HTMLButtonElement>);
				}
			}}
			form={action.form}
			key={actionId}
		>
			{action.children}
		</Button>
	);
}
